import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Cloud,
  Download,
  History,
  Loader2,
  Package,
  Power,
  RotateCcw,
  Save,
  Server,
} from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/uiext";
import { toast } from "sonner";

function fmtAgo(tsSeconds, t) {
  if (!tsSeconds) return "—";
  const ms = Date.now() - tsSeconds * 1000;
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return t("pages.platformSystem.ago.seconds", { n: s });
  if (s < 3600) return t("pages.platformSystem.ago.minutes", { n: Math.floor(s / 60) });
  if (s < 86400) return t("pages.platformSystem.ago.hours", { n: Math.floor(s / 3600) });
  return t("pages.platformSystem.ago.days", { n: Math.floor(s / 86400) });
}

function StateBadge({ state }) {
  const { t } = useTranslation();
  const map = {
    queued: { cls: "", icon: Clock, label: t("pages.platformSystem.state.queued") },
    "fetching-manifest": { cls: "border-sky-500/50 text-sky-500", icon: Cloud, label: t("pages.platformSystem.state.checking") },
    downloading: { cls: "border-sky-500/50 text-sky-500", icon: Download, label: t("pages.platformSystem.state.downloading") },
    applying: { cls: "border-amber-500/50 text-amber-500", icon: Loader2, label: t("pages.platformSystem.state.installing"), spin: true },
    "rolling-back": { cls: "border-amber-500/50 text-amber-500", icon: RotateCcw, label: t("pages.platformSystem.state.rollingBack"), spin: true },
    done: { cls: "border-emerald-500/50 text-emerald-500", icon: CheckCircle2, label: t("pages.platformSystem.state.complete") },
    "up-to-date": { cls: "border-emerald-500/50 text-emerald-500", icon: CheckCircle2, label: t("pages.platformSystem.state.upToDate") },
    failed: { cls: "border-destructive/60 text-destructive", icon: AlertTriangle, label: t("pages.platformSystem.state.failed") },
  };
  const s = map[state] || { cls: "", icon: Clock, label: state || "…" };
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${s.cls}`}>
      <Icon className={`h-3 w-3 ${s.spin ? "animate-spin" : ""}`} />
      {s.label}
    </Badge>
  );
}

// Hotfix Z.11 — emergency reset for stuck channel counters.
//
// Counters live in Mongo and are decremented on call end / BYE. If the
// engine ever crashes (OOM, SIGKILL, deploy without graceful drain)
// while calls are in flight, those counters never decrement and the
// next outbound call legitimately hits "channel limit exceeded" even
// though no calls are actually live. The engine self-heals on cold
// start, but operators shouldn't have to `systemctl restart` for this.
//
// The card shows the rows that have non-zero counts, then lets the
// operator zero them with a single click. Audit-logged. Engine reads
// counters fresh on every reservation so the next datagram sees zeros.
function ChannelsResetCard() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [stuck, setStuck] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/channels/live");
      // Surface rows where stored counter > 0 — these are the candidates.
      const rows = (r.data?.rows || []).filter(
        (x) => (x.total || 0) > 0 || (x.in || 0) > 0 || (x.out || 0) > 0,
      );
      setStuck(rows);
    } catch (e) {
      // Non-fatal — operator can still click reset.
      setStuck([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onReset = async () => {
    const ok = window.confirm(
      t(
        "pages.platformSystem.channelsResetConfirm",
        "Reset ALL stuck channel counters to zero? This is safe when no calls are actually live, and recovers from \"channel limit exceeded\" loops caused by stale counters. Audit-logged.",
      ),
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post("/platform/channels/reset");
      toast.success(
        t("pages.platformSystem.channelsResetDone", "Reset {{n}} counter rows", {
          n: r.data?.reset_rows ?? 0,
        }),
      );
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("pages.platformSystem.channelsResetFailed", "Reset failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="card-channels-reset" className="border-amber-500/30">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-amber-500/10 p-2 mt-0.5">
            <RotateCcw className="h-5 w-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">
              {t("pages.platformSystem.channelsResetTitle", "Reset stuck channel counters")}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {t(
                "pages.platformSystem.channelsResetDesc",
                "Use this if outbound calls fail with \"channel limit exceeded\" even though no calls are actually live. This zeros every counter row across all tenants/trunks. Safe when nothing is in flight; will under-count by 1 if used while a real call is mid-dialog.",
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="gap-1 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
            onClick={onReset}
            disabled={busy}
            data-testid="btn-channels-reset"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {t("pages.platformSystem.channelsResetBtn", "Reset all counters")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="btn-channels-reset-refresh"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.refresh", "Refresh")}
          </Button>
          <Badge variant="outline" data-testid="badge-channels-stuck-count">
            {t("pages.platformSystem.channelsStuckCount", "{{n}} non-zero rows", {
              n: stuck.length,
            })}
          </Badge>
        </div>
        {stuck.length > 0 && (
          <div className="rounded-md border border-border/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.platformSystem.channelsScope", "Scope")}</TableHead>
                  <TableHead>{t("pages.platformSystem.channelsName", "Name")}</TableHead>
                  <TableHead className="text-right">
                    {t("pages.platformSystem.channelsTotal", "Total")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pages.platformSystem.channelsCap", "Cap")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stuck.map((r) => (
                  <TableRow key={`${r.scope}-${r.scope_id}`}>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {r.scope}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{r.name || r.scope_id}</TableCell>
                    <TableCell className="text-right font-mono">{r.total ?? 0}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {r.cap_total ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlatformSystem() {
  const { t } = useTranslation();
  const [version, setVersion] = useState({ current: "…", releases: [] });
  const [urlCfg, setUrlCfg] = useState({ manifest_url: "", env_manifest_url: "", effective: null });
  const [editUrl, setEditUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);

  const [checking, setChecking] = useState(false);
  const [latest, setLatest] = useState(null);
  const [updateAvail, setUpdateAvail] = useState(false);

  const [task, setTask] = useState(null);  // active upgrade/rollback task
  const [logOpen, setLogOpen] = useState(false);
  const pollRef = useRef(null);

  // Phase 6z (Hotfix S) — engine restart
  const [engineRestartOpen, setEngineRestartOpen] = useState(false);
  const [engineRestarting, setEngineRestarting] = useState(false);

  const restartEngine = useCallback(async () => {
    setEngineRestarting(true);
    try {
      const r = await api.post("/platform/system/engine-restart");
      const mgr = r.data?.manager || "unknown";
      toast.success(t("pages.platformSystem.engineRestartOk", { manager: mgr }));
      setEngineRestartOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        const msg = detail.message || e?.message || "Restart failed";
        if (detail.hint) {
          toast.error(msg, { description: detail.hint, duration: 12000 });
        } else {
          toast.error(msg);
        }
      } else {
        toast.error(typeof detail === "string" ? detail : (e?.message || "Restart failed"));
      }
    } finally {
      setEngineRestarting(false);
    }
  }, [t]);

  /* -------- data loaders -------- */
  const loadVersion = useCallback(async () => {
    try {
      const r = await api.get("/platform/system/version");
      setVersion(r.data);
    } catch { /* dev mode may lack endpoints */ }
  }, []);
  const loadUrl = useCallback(async () => {
    try {
      const r = await api.get("/platform/system/release-url");
      setUrlCfg(r.data);
      setEditUrl(r.data.manifest_url || "");
    } catch { /* */ }
  }, []);

  useEffect(() => { loadVersion(); loadUrl(); }, [loadVersion, loadUrl]);

  /* -------- task polling -------- */
  const pollTask = useCallback((tid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    // Track the target version so we can self-heal if the task row is lost
    // mid-upgrade (the API gets restarted by the upgrade itself).
    let targetStamp = null;
    let missingCount = 0;
    const previousStamp = version?.current;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/platform/system/task/${tid}`);
        setTask(r.data);
        missingCount = 0;
        if (r.data.target_stamp) targetStamp = r.data.target_stamp;
        if (["done", "failed", "up-to-date"].includes(r.data.state)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadVersion();
          if (r.data.state === "done") {
            // Hotfix Z.20 — tell the auto-update poll to re-check
            // immediately so the header "Update available" pill drops
            // within seconds instead of waiting for the next hourly
            // tick. Any other consumer of this event can hook in too.
            try {
              window.dispatchEvent(new CustomEvent("elswitch:version-changed"));
            } catch { /* older browsers — ignore */ }
            toast.success(r.data.kind === "upgrade"
              ? t("pages.platformSystem.upgradeComplete")
              : t("pages.platformSystem.rollbackComplete"));
          } else if (r.data.state === "failed") {
            toast.error(`${r.data.kind} failed: ${r.data.error || "unknown error"}`);
          } else {
            toast.info(t("pages.platformSystem.alreadyLatest"));
          }
        } else if (r.data.state === "interrupted") {
          // API was restarted mid-task. Cross-check the version endpoint —
          // if it now matches the target, declare success.
          const v = await api.get("/platform/system/version").catch(() => null);
          const currentStamp = v?.data?.current;
          if (targetStamp && currentStamp === targetStamp) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setTask({ ...r.data, state: "done", progress: 100 });
            loadVersion();
            // Hotfix Z.20 — same broadcast as the happy path.
            try {
              window.dispatchEvent(new CustomEvent("elswitch:version-changed"));
            } catch { /* older browsers — ignore */ }
            toast.success(t("pages.platformSystem.upgradeComplete"));
          }
        }
      } catch (err) {
        // On 404 (task row missing after an API restart) fall back to
        // comparing /version against whatever we last saw.
        if (err?.response?.status === 404) {
          missingCount += 1;
          const v = await api.get("/platform/system/version").catch(() => null);
          const currentStamp = v?.data?.current;
          if (currentStamp && currentStamp !== previousStamp) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setTask({ id: tid, kind: "upgrade", state: "done", progress: 100, log: [] });
            loadVersion();
            // Hotfix Z.20 — 404-recovery path also completes an upgrade.
            try {
              window.dispatchEvent(new CustomEvent("elswitch:version-changed"));
            } catch { /* older browsers — ignore */ }
            toast.success(t("pages.platformSystem.upgradeComplete"));
            return;
          }
          if (missingCount >= 20) {
            // ~30s without the task row or any version change — give up.
            clearInterval(pollRef.current);
            pollRef.current = null;
            setTask({
              id: tid,
              kind: "upgrade",
              state: "failed",
              error: "Task state lost and version did not change.",
              log: [],
            });
            toast.error(t("pages.platformSystem.upgradeTimeout"));
          }
        }
      }
    }, 1500);
  }, [loadVersion, version?.current, t]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  /* -------- actions -------- */
  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      await api.put("/platform/system/release-url", { manifest_url: editUrl });
      toast.success(t("pages.platformSystem.releaseUrlSaved"));
      await loadUrl();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pages.platformSystem.saveFailed"));
    } finally {
      setSavingUrl(false);
    }
  };

  const checkNow = async () => {
    setChecking(true);
    setLatest(null);
    try {
      const r = await api.get("/platform/system/check");
      setLatest(r.data.latest);
      setUpdateAvail(!!r.data.update_available);
      if (r.data.update_available) {
        toast.info(t("pages.platformSystem.updateAvailable", { version: r.data.latest.version }));
      } else {
        toast.success(t("pages.platformSystem.youreLatest"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pages.platformSystem.checkFailed"));
    } finally {
      setChecking(false);
    }
  };

  const upgrade = async () => {
    try {
      const r = await api.post("/platform/system/upgrade");
      setTask({ id: r.data.task_id, state: "queued", log: [] });
      setLogOpen(true);
      pollTask(r.data.task_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pages.platformSystem.upgradeFailedStart"));
    }
  };

  const rollback = async (target) => {
    if (!window.confirm(target
      ? t("pages.platformSystem.rollbackPrompt", { stamp: target })
      : t("pages.platformSystem.rollbackPromptPrev"))) return;
    try {
      const r = await api.post("/platform/system/rollback", target ? { target } : {});
      setTask({ id: r.data.task_id, state: "queued", log: [] });
      setLogOpen(true);
      pollTask(r.data.task_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pages.platformSystem.rollbackFailedStart"));
    }
  };

  const isDev = version.current === "development";

  return (
    <div data-testid="page-platform-system" className="space-y-6">
      <PageHeader title={t("pages.platformSystem.title")} icon={Server} />

      {isDev && (
        <Card className="border-amber-500/30">
          <CardContent className="p-4 flex gap-3 items-start text-sm">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div>
              <div className="font-semibold">{t("pages.platformSystem.devMode")}</div>
              <div className="text-muted-foreground">
                {t("pages.platformSystem.devModeDesc", {
                  path: "/opt/elswitch",
                  installer: "deploy/install.sh",
                  cta: t("pages.platformSystem.checkForUpdates"),
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* -------- Current version card -------- */}
      <Card data-testid="card-current-version">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-primary/10 p-2">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                {t("pages.platformSystem.currentlyRunning")}
              </div>
              <div className="text-xl font-semibold font-mono" data-testid="current-version">
                {version.current}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={checkNow} disabled={checking || !urlCfg.effective}
                    data-testid="btn-check-updates" className="gap-1">
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
              {t("pages.platformSystem.checkForUpdates")}
            </Button>
            {updateAvail && latest && (
              <Button onClick={upgrade} variant="default"
                      className="gap-1 bg-primary" data-testid="btn-upgrade">
                <Download className="h-4 w-4" />
                {t("pages.platformSystem.upgradeTo", { version: latest.version })}
              </Button>
            )}
          </div>
          {latest && (
            <div className="rounded-md border border-border/60 p-3 bg-muted/30 text-sm space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs uppercase">{t("pages.platformSystem.latestAvailable")}</span>
                <span className="font-mono font-semibold">{latest.stamp}</span>
                {updateAvail ? (
                  <Badge variant="outline" className="border-sky-500/50 text-sky-500">
                    {t("pages.platformSystem.updateReady")}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
                    {t("pages.platformSystem.youreCurrent")}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("pages.platformSystem.built")} {latest.built_at} · {(latest.size_bytes / 1024 / 1024).toFixed(2)} MB ·
                sha256 <code className="text-[10px]">{latest.sha256?.slice(0, 12)}…</code>
              </div>
              {latest.notes && (
                <pre className="text-xs whitespace-pre-wrap pt-1 text-muted-foreground">
                  {latest.notes}
                </pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* -------- Engine control card (Phase 6z / Hotfix S) -------- */}
      <Card data-testid="card-engine-control">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-amber-500/10 p-2">
              <Power className="h-5 w-5 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                {t("pages.platformSystem.engineControl")}
              </div>
              <div className="text-base font-semibold">
                {t("pages.platformSystem.engineControlTitle")}
              </div>
            </div>
            <Button
              variant="outline"
              className="gap-1 border-amber-500/40 text-amber-600 hover:text-amber-700"
              onClick={() => setEngineRestartOpen(true)}
              data-testid="btn-engine-restart"
            >
              <RotateCcw className="h-4 w-4" />
              {t("pages.platformSystem.restartEngine")}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            {t("pages.platformSystem.engineControlDesc")}
          </div>
        </CardContent>
      </Card>

      {/* -------- Restart engine confirmation dialog -------- */}
      <Dialog open={engineRestartOpen} onOpenChange={setEngineRestartOpen}>
        <DialogContent data-testid="dialog-engine-restart">
          <DialogHeader>
            <DialogTitle>{t("pages.platformSystem.restartEngineConfirmTitle")}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>{t("pages.platformSystem.restartEngineConfirmBody")}</p>
            <p className="text-amber-600">
              <AlertTriangle className="inline h-4 w-4 mr-1 align-text-bottom" />
              {t("pages.platformSystem.restartEngineConfirmWarning")}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEngineRestartOpen(false)}
              disabled={engineRestarting}
              data-testid="btn-engine-restart-cancel"
            >
              {t("pages.platformSystem.close")}
            </Button>
            <Button
              variant="default"
              onClick={restartEngine}
              disabled={engineRestarting}
              className="gap-1"
              data-testid="btn-engine-restart-confirm"
            >
              {engineRestarting
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RotateCcw className="h-4 w-4" />}
              {t("pages.platformSystem.restartEngine")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Release-URL configuration -------- */}
      <Card data-testid="card-release-url">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-primary" />
            <div className="font-semibold">{t("pages.platformSystem.releaseSource")}</div>
          </div>
          <div className="text-xs text-muted-foreground">
            {t("pages.platformSystem.releaseSourceDesc")}
          </div>
          <div className="flex gap-2">
            <Input
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              placeholder="https://<bucket>.<region>.vultrobjects.com/manifest.json"
              data-testid="input-release-url"
            />
            <Button onClick={saveUrl} disabled={savingUrl || editUrl === urlCfg.manifest_url}
                    className="gap-1" data-testid="btn-save-release-url">
              <Save className="h-4 w-4" />{t("pages.platformSystem.save")}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            <div>{t("pages.platformSystem.effective")}: <code>{urlCfg.effective || t("pages.platformSystem.notConfigured")}</code></div>
            {urlCfg.env_manifest_url && (
              <div>{t("pages.platformSystem.envFallback")}: <code>{urlCfg.env_manifest_url}</code></div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* -------- Installed releases table -------- */}
      <Card data-testid="card-releases">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <div className="font-semibold">{t("pages.platformSystem.installedReleases")}</div>
            <span className="text-xs text-muted-foreground">
              {t("pages.platformSystem.installedReleasesNote")}
            </span>
          </div>
          {version.releases.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">
              {isDev
                ? t("pages.platformSystem.noReleasesDev")
                : t("pages.platformSystem.noReleases")}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.platformSystem.release")}</TableHead>
                    <TableHead className="w-28">{t("pages.platformSystem.status")}</TableHead>
                    <TableHead className="w-36">{t("pages.platformSystem.installed")}</TableHead>
                    <TableHead className="w-28 text-right">{t("pages.platformSystem.action")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {version.releases.map((r) => (
                    <TableRow key={r.stamp} data-testid={`release-row-${r.stamp}`}>
                      <TableCell className="font-mono">{r.stamp}</TableCell>
                      <TableCell>
                        {r.is_current ? (
                          <Badge variant="outline"
                                 className="border-emerald-500/50 text-emerald-500 gap-1">
                            <CheckCircle2 className="h-3 w-3" /> {t("pages.platformSystem.active")}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            {t("pages.platformSystem.standby")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fmtAgo(r.installed_at, t)}
                      </TableCell>
                      <TableCell className="text-right">
                        {!r.is_current && (
                          <Button size="sm" variant="outline" className="gap-1"
                                  onClick={() => rollback(r.stamp)}
                                  data-testid={`btn-rollback-${r.stamp}`}>
                            <RotateCcw className="h-3 w-3" /> {t("pages.platformSystem.rollback")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hotfix Z.11 — emergency channel-counter reset. */}
      <ChannelsResetCard />

      {/* -------- Upgrade progress dialog -------- */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-2xl" data-testid="dialog-task-progress">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {task?.kind === "rollback"
                ? t("pages.platformSystem.rollbackProgress")
                : t("pages.platformSystem.upgradeProgress")}
              {task && <StateBadge state={task.state} />}
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-md bg-black/60 text-slate-200 font-mono text-xs p-3 max-h-96 overflow-auto whitespace-pre-wrap">
            {(task?.log || []).length === 0 ? (
              <div className="text-slate-500">{t("pages.platformSystem.waitingOutput")}</div>
            ) : (
              (task?.log || []).map((l, i) => (
                <div key={i}>{l.msg}</div>
              ))
            )}
          </div>
          {task?.error && (
            <div className="text-sm text-destructive mt-2">{t("pages.platformSystem.errorLabel")}: {task.error}</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpen(false)}>{t("pages.platformSystem.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
